import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Palette, Upload, DollarSign, ShoppingCart } from 'lucide-react';
import api from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Color palette (21 colors)
const COLORS = [
  { name: 'Black', hex: '#000000' },
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Red', hex: '#DC2626' },
  { name: 'Navy Blue', hex: '#1E3A8A' },
  { name: 'Royal Blue', hex: '#2563EB' },
  { name: 'Forest Green', hex: '#065F46' },
  { name: 'Olive Green', hex: '#4B5320' },
  { name: 'Kelly Green', hex: '#4CBB17' },
  { name: 'Burgundy', hex: '#800020' },
  { name: 'Gold', hex: '#FFD700' },
  { name: 'Silver', hex: '#C0C0C0' },
  { name: 'Purple', hex: '#7C3AED' },
  { name: 'Orange', hex: '#EA580C' },
  { name: 'Cream', hex: '#F5F5DC' },
  { name: 'Mustard', hex: '#FFDB58' },
  { name: 'Tan', hex: '#D2B48C' },
  { name: 'Brown', hex: '#92400E' },
  { name: 'Gray', hex: '#6B7280' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Soft Pink', hex: '#FFC0CB' },
  { name: 'Fuchsia', hex: '#FF00FF' }
];

const MATERIALS = [
  { name: 'Standard', value: null },
  { name: 'Leather', value: 'leather' },
  { name: 'Suede', value: 'suede' },
  { name: 'Wool', value: 'wool' }
];

const LOGO_LOCATIONS = [
  { name: 'Left Chest', value: 'left_chest', price: 50 },
  { name: 'Right Chest', value: 'right_chest', price: 50 },
  { name: 'Left Wrist', value: 'left_wrist', price: 50 },
  { name: 'Right Wrist', value: 'right_wrist', price: 50 },
  { name: 'Back', value: 'back', price: 125 }
];

const ProductCustomizer = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Customization state
  const [customizations, setCustomizations] = useState({
    body: { color: 'Black', material: null },
    sleeves: { color: 'Black', material: null },
    ribbing: { color: 'Black', material: null },
    lining: { color: 'Cream', material: null },
    valley_logo: { color: 'White', material: null },
    armpit_inserts: { color: 'Black', material: null }
  });
  
  const [logoPlacement, setLogoPlacement] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeElement, setActiveElement] = useState('body');
  const [viewMode, setViewMode] = useState('front'); // 'front' or 'back'

  useEffect(() => {
    fetchProduct();
  }, [handle]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/shopify/products/${handle}`);
      
      if (response.data && response.data.product) {
        setProduct(response.data.product);
      } else {
        toast.error('Product not found');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getCustomizableElements = () => {
    // Valley products have additional customizable elements
    const isValleyProduct = handle.includes('valley');
    
    const baseElements = [
      { id: 'body', name: 'Body', icon: '👕' },
      { id: 'sleeves', name: 'Sleeves', icon: '💪' },
      { id: 'ribbing', name: 'Ribbing', icon: '⚡' },
      { id: 'lining', name: 'Lining', icon: '🧥' }
    ];
    
    if (isValleyProduct) {
      baseElements.push(
        { id: 'valley_logo', name: 'Valley Logo', icon: '🔤' },
        { id: 'armpit_inserts', name: 'Armpit Inserts', icon: '📐' }
      );
    }
    
    return baseElements;
  };

  const handleColorChange = (element, color) => {
    setCustomizations(prev => ({
      ...prev,
      [element]: { ...prev[element], color }
    }));
  };

  const handleMaterialChange = (element, material) => {
    setCustomizations(prev => ({
      ...prev,
      [element]: { ...prev[element], material }
    }));
  };

  const toggleLogoPlacement = (location, price) => {
    setLogoPlacement(prev => {
      const exists = prev.find(p => p.location === location);
      if (exists) {
        return prev.filter(p => p.location !== location);
      } else {
        return [...prev, { location, price_addon: price, logo_url: null, logo_text: null }];
      }
    });
  };

  const calculateTotalPrice = () => {
    if (!product) return 0;
    
    // Try multiple possible price locations in the product object
    let basePrice = 0;
    if (product.priceRange?.minVariantPrice?.amount) {
      basePrice = parseFloat(product.priceRange.minVariantPrice.amount);
    } else if (product.variants?.[0]?.price) {
      basePrice = parseFloat(product.variants[0].price);
    } else if (product.price) {
      basePrice = parseFloat(product.price);
    }
    
    const logoPrice = logoPlacement.reduce((sum, logo) => sum + logo.price_addon, 0);
    
    return basePrice + logoPrice;
  };

  const handleSaveCustomization = async () => {
    try {
      setSaving(true);
      
      if (!selectedSize && product?.variants?.length > 1) {
        toast.error('Please select a size');
        return;
      }
      
      const customizationData = {
        base_product_handle: handle,
        base_product_name: product.title,
        base_price: parseFloat(product.priceRange?.minVariantPrice?.amount || 0),
        customizations: Object.entries(customizations).map(([element_name, data]) => ({
          element_name,
          color: data.color,
          material: data.material
        })),
        logo_placements: logoPlacement,
        variant_size: selectedSize
      };
      
      const response = await api.post('/custom-products/create', customizationData);
      
      if (response.data.success) {
        toast.success('Customization saved! Adding to cart...');
        // TODO: Add to cart with custom product ID
        // For now, navigate to product page
        setTimeout(() => {
          navigate(`/products/${handle}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving customization:', error);
      toast.error('Failed to save customization');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <div className="text-cream text-xl">Loading customizer...</div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const totalPrice = calculateTotalPrice();
  const customizableElements = getCustomizableElements();

  return (
    <div className="min-h-screen bg-charcoal">
      <Header />
      
      <div className="pt-24 pb-16 px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Page Header */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate(`/products/${handle}`)}
              className="text-warmSage hover:text-cream mb-4"
            >
              ← Back to Product
            </Button>
            <h1 className="text-4xl font-black text-cream mb-2">
              CUSTOMIZE YOUR {product?.title?.toUpperCase() || 'PRODUCT'}
            </h1>
            <p className="text-warmGray text-lg">
              Create your unique piece. Choose colors, materials, and add custom embroidery.
            </p>
          </div>

          <div className="grid lg:grid-cols-[45%_55%] gap-8">
            {/* Preview Section */}
            <div className="space-y-6">
              <Card className="bg-warmGray/10 border-warmSage/20">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-cream flex items-center gap-2">
                      <Palette className="h-6 w-6 text-warmSage" />
                      Preview
                    </h3>
                    
                    {/* Front/Back Toggle */}
                    {handle.includes('valley') && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setViewMode('front')}
                          size="sm"
                          variant={viewMode === 'front' ? 'default' : 'outline'}
                          className={viewMode === 'front' 
                            ? 'bg-warmSage text-charcoal hover:bg-warmSage/90'
                            : 'border-warmSage/30 text-cream hover:bg-warmSage/20'
                          }
                        >
                          Front
                        </Button>
                        <Button
                          onClick={() => setViewMode('back')}
                          size="sm"
                          variant={viewMode === 'back' ? 'default' : 'outline'}
                          className={viewMode === 'back'
                            ? 'bg-warmSage text-charcoal hover:bg-warmSage/90'
                            : 'border-warmSage/30 text-cream hover:bg-warmSage/20'
                          }
                        >
                          Back
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Product Image with Layered Preview */}
                  <div className="relative aspect-square bg-white rounded-lg overflow-hidden mb-6 border-2 border-warmSage/20">
                    <div className="relative w-full h-full">
                      {/* VALLEY BOMBER - Layered system with separate images */}
                      {handle.includes('valley') && viewMode === 'front' && (
                        <>
                          {/* FRONT VIEW LAYERS */}
                          {/* Layer 1: Body - colored background behind PNG */}
                          <div className="absolute inset-0 w-full h-full z-10">
                            {/* Colored background */}
                            <div 
                              className="absolute inset-0 w-full h-full"
                              style={{ backgroundColor: COLORS.find(c => c.name === customizations.body.color)?.hex }}
                            />
                            {/* PNG mask/outline on top */}
                            <img 
                              src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/hwtmuiy4_IMG_4897.png"
                              alt="Body Layer"
                              className="absolute inset-0 w-full h-full object-contain"
                              style={{ mixBlendMode: 'multiply' }}
                            />
                          </div>
                          
                          {/* Layer 2: Sleeves - with color overlay */}
                          <div className="absolute inset-0 w-full h-full z-20">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/3dqjrg5t_IMG_4896.png"
                              alt="Sleeves Layer"
                              className="w-full h-full object-contain"
                            />
                            <div 
                              className="absolute inset-0 w-full h-full mix-blend-multiply"
                              style={{ backgroundColor: COLORS.find(c => c.name === customizations.sleeves.color)?.hex }}
                            />
                          </div>
                          
                          {/* Layer 3: Ribbing - with color overlay */}
                          <div className="absolute inset-0 w-full h-full z-30">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/rafiej60_IMG_4898.png"
                              alt="Ribbing Layer"
                              className="w-full h-full object-contain"
                            />
                            <div 
                              className="absolute inset-0 w-full h-full mix-blend-multiply"
                              style={{ backgroundColor: COLORS.find(c => c.name === customizations.ribbing.color)?.hex }}
                            />
                          </div>
                          
                          {/* Layer 4: Armpit Inserts - with color overlay */}
                          <div className="absolute inset-0 w-full h-full z-35">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/ais282fc_IMG_4899.png"
                              alt="Armpit Inserts Layer"
                              className="w-full h-full object-contain"
                            />
                            <div 
                              className="absolute inset-0 w-full h-full mix-blend-multiply"
                              style={{ backgroundColor: COLORS.find(c => c.name === customizations.armpit_inserts?.color)?.hex }}
                            />
                          </div>
                          
                          {/* Layer 5: Base Outline - always on top, no color */}
                          <img 
                            src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/fao2r5b2_IMG_4895.png"
                            alt="Outline"
                            className="absolute inset-0 w-full h-full object-contain z-50"
                          />
                        </>
                      )}
                      
                      {/* VALLEY BOMBER - BACK VIEW */}
                      {handle.includes('valley') && viewMode === 'back' && (
                        <>
                          {/* BACK VIEW LAYERS */}
                          {/* Layer 1: Body (back) - with color overlay */}
                          <div className="absolute inset-0 w-full h-full z-10">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/rjyjbm99_IMG_4893.png"
                              alt="Body Layer Back"
                              className="w-full h-full object-contain"
                            />
                            <div 
                              className="absolute inset-0 w-full h-full mix-blend-multiply"
                              style={{ backgroundColor: COLORS.find(c => c.name === customizations.body.color)?.hex }}
                            />
                          </div>
                          
                          {/* Layer 2: Sleeves (back) - with color overlay */}
                          <div className="absolute inset-0 w-full h-full z-20">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/dw23t4uc_IMG_4890.png"
                              alt="Sleeves Layer Back"
                              className="w-full h-full object-contain"
                            />
                            <div 
                              className="absolute inset-0 w-full h-full mix-blend-multiply"
                              style={{ backgroundColor: COLORS.find(c => c.name === customizations.sleeves.color)?.hex }}
                            />
                          </div>
                          
                          {/* Layer 3: Ribbing (back) - with color overlay */}
                          <div className="absolute inset-0 w-full h-full z-30">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/o56h1bp0_IMG_4892.png"
                              alt="Ribbing Layer Back"
                              className="w-full h-full object-contain"
                            />
                            <div 
                              className="absolute inset-0 w-full h-full mix-blend-multiply"
                              style={{ backgroundColor: COLORS.find(c => c.name === customizations.ribbing.color)?.hex }}
                            />
                          </div>
                          
                          {/* Layer 4: Armpit Inserts (back) - with color overlay */}
                          <div className="absolute inset-0 w-full h-full z-35">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/oph7uqi6_IMG_4891.png"
                              alt="Armpit Inserts Layer Back"
                              className="w-full h-full object-contain"
                            />
                            <div 
                              className="absolute inset-0 w-full h-full mix-blend-multiply"
                              style={{ backgroundColor: COLORS.find(c => c.name === customizations.armpit_inserts?.color)?.hex }}
                            />
                          </div>
                          
                          {/* Layer 5: Base Outline (back) - always on top, no color */}
                          <img 
                            src="https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/q833r7v2_IMG_4894.png"
                            alt="Outline Back"
                            className="absolute inset-0 w-full h-full object-contain z-50"
                          />
                        </>
                      )}
                      
                      {/* COLLEGIATE BOMBER - Use template */}
                      {handle.includes('collegiate') && (
                        <img 
                          src='https://customer-assets.emergentagent.com/job_prsvr-store/artifacts/kdwkqjzu_IMG_4887.jpeg'
                          alt={product?.title || 'Product'}
                          className="w-full h-full object-contain relative z-10"
                        />
                      )}
                      
                      {/* OTHER PRODUCTS - Use Shopify image */}
                      {!handle.includes('valley') && !handle.includes('collegiate') && (
                        <img 
                          src={product?.images?.[0]?.url || 'https://via.placeholder.com/500'}
                          alt={product?.title || 'Product'}
                          className="w-full h-full object-contain relative z-10"
                        />
                      )}
                      
                      {/* Color overlays for Collegiate (no separate layers yet) */}
                      {handle.includes('collegiate') && (
                        <div className="absolute inset-0 z-20 pointer-events-none">
                          {/* Body color */}
                          <div 
                            className="absolute mix-blend-multiply"
                            style={{ 
                              backgroundColor: COLORS.find(c => c.name === customizations.body.color)?.hex,
                              opacity: 0.65,
                              top: '24%',
                              left: '34%',
                              width: '32%',
                              height: '38%',
                              clipPath: 'polygon(15% 0%, 85% 0%, 100% 8%, 100% 92%, 85% 100%, 15% 100%, 0% 92%, 0% 8%)'
                            }}
                          />
                          {/* Sleeves */}
                          <div 
                            className="absolute mix-blend-multiply"
                            style={{ 
                              backgroundColor: COLORS.find(c => c.name === customizations.sleeves.color)?.hex,
                              opacity: 0.65,
                              top: '26%',
                              left: '16%',
                              width: '18%',
                              height: '44%',
                              clipPath: 'polygon(40% 0%, 100% 5%, 95% 100%, 20% 95%)'
                            }}
                          />
                          <div 
                            className="absolute mix-blend-multiply"
                            style={{ 
                              backgroundColor: COLORS.find(c => c.name === customizations.sleeves.color)?.hex,
                              opacity: 0.65,
                              top: '26%',
                              right: '16%',
                              width: '18%',
                              height: '44%',
                              clipPath: 'polygon(0% 5%, 60% 0%, 80% 95%, 5% 100%)'
                            }}
                          />
                        </div>
                      )}
                      
                      {/* For other products, use overlay method */}
                      {!handle.includes('valley') && !handle.includes('collegiate') && (
                        <>
                          <div 
                            className="absolute inset-0 mix-blend-multiply opacity-60 pointer-events-none"
                            style={{ 
                              backgroundColor: COLORS.find(c => c.name === customizations.body.color)?.hex,
                              maskImage: 'radial-gradient(ellipse 60% 70% at 50% 45%, black 40%, transparent 70%)',
                              WebkitMaskImage: 'radial-gradient(ellipse 60% 70% at 50% 45%, black 40%, transparent 70%)'
                            }}
                          />
                          <div 
                            className="absolute inset-0 mix-blend-multiply opacity-50 pointer-events-none"
                            style={{ 
                              backgroundColor: COLORS.find(c => c.name === customizations.sleeves.color)?.hex,
                              maskImage: 'radial-gradient(ellipse 30% 60% at 20% 50%, black 30%, transparent 50%), radial-gradient(ellipse 30% 60% at 80% 50%, black 30%, transparent 50%)',
                              WebkitMaskImage: 'radial-gradient(ellipse 30% 60% at 20% 50%, black 30%, transparent 50%), radial-gradient(ellipse 30% 60% at 80% 50%, black 30%, transparent 50%)'
                            }}
                          />
                        </>
                      )}
                    </div>
                    
                    {/* Selected colors legend */}
                    <div className="absolute top-4 right-4 space-y-2 bg-black/80 p-3 rounded-lg">
                      <p className="text-xs text-warmSage font-bold mb-2">CURRENT SELECTION</p>
                      {Object.entries(customizations).map(([element, data]) => (
                        <div key={element} className="flex items-center gap-2">
                          <div 
                            className="w-5 h-5 rounded border-2 border-white shadow-lg" 
                            style={{ backgroundColor: COLORS.find(c => c.name === data.color)?.hex }}
                          />
                          <div className="text-xs">
                            <span className="text-white font-bold capitalize block">
                              {element.replace('_', ' ')}
                            </span>
                            <span className="text-warmGray text-[10px]">
                              {data.color}
                              {data.material && ` - ${data.material}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* "Live Preview" badge */}
                    <div className="absolute bottom-4 left-4 bg-warmSage text-charcoal px-3 py-1 rounded-full text-xs font-bold">
                      🎨 LIVE PREVIEW
                    </div>
                  </div>

                  {/* Price Summary */}
                  <div className="bg-charcoal/50 p-6 rounded-lg">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-warmGray">Base Price:</span>
                      <span className="text-cream font-bold">${(calculateTotalPrice() - logoPlacement.reduce((sum, logo) => sum + logo.price_addon, 0)).toFixed(2)}</span>
                    </div>
                    
                    {logoPlacement.length > 0 && (
                      <div className="border-t border-warmSage/20 pt-4 mb-4">
                        <p className="text-warmGray mb-2">Embroidery:</p>
                        {logoPlacement.map((logo, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm mb-1">
                            <span className="text-warmGray capitalize">{logo.location.replace('_', ' ')}</span>
                            <span className="text-cream">+${logo.price_addon}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="border-t border-warmSage/20 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-cream">Total:</span>
                        <span className="text-3xl font-black text-warmSage">${totalPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Customization Controls - Combined in one card */}
            <div className="space-y-6">
              <Card className="bg-warmGray/10 border-warmSage/20">
                <CardContent className="p-6 space-y-6">
                  {/* Element Selector */}
                  <div>
                    <h3 className="text-lg font-bold text-cream mb-3">Select Element</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {customizableElements.map((element) => (
                        <Button
                          key={element.id}
                          onClick={() => setActiveElement(element.id)}
                          variant={activeElement === element.id ? 'default' : 'outline'}
                          className={`h-auto py-3 ${
                            activeElement === element.id
                              ? 'bg-warmSage text-charcoal hover:bg-warmSage/90'
                              : 'border-warmSage/30 text-cream hover:bg-warmSage/20'
                          }`}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <span className="text-xl">{element.icon}</span>
                            <span className="font-bold text-xs">{element.name}</span>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Color Picker */}
                  <div className="border-t border-warmSage/20 pt-6">
                    <h3 className="text-lg font-bold text-cream mb-3">
                      Color for {customizableElements.find(e => e.id === activeElement)?.name}
                    </h3>
                    <div className="grid grid-cols-7 gap-2">
                      {COLORS.map((color) => (
                        <button
                          key={color.name}
                          onClick={() => handleColorChange(activeElement, color.name)}
                          className={`aspect-square rounded-lg border-2 transition-all hover:scale-110 ${
                            customizations[activeElement]?.color === color.name
                              ? 'border-warmSage ring-2 ring-warmSage ring-offset-2 ring-offset-charcoal scale-110'
                              : 'border-warmGray/30'
                          }`}
                          style={{ backgroundColor: color.hex }}
                          title={color.name}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-warmGray mt-3">
                      Selected: <span className="text-cream font-bold">{customizations[activeElement]?.color}</span>
                    </p>
                  </div>

                  {/* Material Selector */}
                  <div className="border-t border-warmSage/20 pt-6">
                    <h3 className="text-lg font-bold text-cream mb-3">Material</h3>
                    <div className="grid grid-cols-4 gap-2">
                      {MATERIALS.map((material) => (
                        <Button
                          key={material.name}
                          onClick={() => handleMaterialChange(activeElement, material.value)}
                          variant={customizations[activeElement]?.material === material.value ? 'default' : 'outline'}
                          size="sm"
                          className={`${
                            customizations[activeElement]?.material === material.value
                              ? 'bg-warmSage text-charcoal hover:bg-warmSage/90'
                              : 'border-warmSage/30 text-cream hover:bg-warmSage/20'
                          }`}
                        >
                          {material.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Logo Placement */}
              <Card className="bg-warmGray/10 border-warmSage/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-cream mb-4 flex items-center gap-2">
                    <Upload className="h-5 w-5 text-warmSage" />
                    Custom Logo / Embroidery
                  </h3>
                  <p className="text-sm text-warmGray mb-4">
                    Add your company logo or custom embroidery to your jacket
                  </p>
                  <div className="space-y-3">
                    {LOGO_LOCATIONS.map((location) => {
                      const isSelected = logoPlacement.find(p => p.location === location.value);
                      return (
                        <div 
                          key={location.value}
                          className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-warmSage bg-warmSage/10' 
                              : 'border-warmGray/30 hover:border-warmSage/50'
                          }`}
                          onClick={() => toggleLogoPlacement(location.value, location.price)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected ? 'border-warmSage bg-warmSage' : 'border-warmGray/50'
                            }`}>
                              {isSelected && <span className="text-charcoal text-xs">✓</span>}
                            </div>
                            <span className="text-cream font-medium">{location.name}</span>
                          </div>
                          <Badge className="bg-warmSage/20 text-warmSage border-warmSage/30">
                            +${location.price}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                  
                  {logoPlacement.length > 0 && (
                    <div className="mt-4 p-4 bg-charcoal/50 rounded-lg">
                      <p className="text-sm text-warmGray mb-2">
                        📧 Upload your logo after checkout - we'll send you instructions via email
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Size Selection */}
              {product.variants && product.variants.length > 1 && (
                <Card className="bg-warmGray/10 border-warmSage/20">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-cream mb-4">Select Size</h3>
                    <div className="grid grid-cols-4 gap-3">
                      {product.variants.map((variant) => (
                        <Button
                          key={variant.id}
                          onClick={() => setSelectedSize(variant.title)}
                          variant={selectedSize === variant.title ? 'default' : 'outline'}
                          className={`${
                            selectedSize === variant.title
                              ? 'bg-warmSage text-charcoal hover:bg-warmSage/90'
                              : 'border-warmSage/30 text-cream hover:bg-warmSage/20'
                          }`}
                        >
                          {variant.title}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4">
                <Button
                  onClick={handleSaveCustomization}
                  disabled={saving}
                  className="flex-1 bg-warmSage text-charcoal hover:bg-warmSage/90 font-black text-lg py-6"
                >
                  {saving ? (
                    'SAVING...'
                  ) : (
                    <>
                      <ShoppingCart className="mr-2 h-5 w-5" />
                      ADD TO CART - ${totalPrice.toFixed(2)}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductCustomizer;
